import { getAtribucion } from './atribucion';
import { getFirestore, collection, doc, addDoc, getDoc, getDocs,
         updateDoc, setDoc, query, where, orderBy, serverTimestamp, deleteDoc, onSnapshot, arrayUnion, writeBatch } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import app, { functions } from './firebase';

export const db = getFirestore(app);

/* ── Helpers ──────────────────────────────────────── */
const pad2 = (value: number) => String(value).padStart(2, '0');
const formatDateValue = (date: Date) => `${pad2(date.getDate())}-${pad2(date.getMonth() + 1)}-${date.getFullYear()}`;
export const fmtDate = (value: any) => {
  if (value == null || value === '') return '—';
  if (value?.toDate) value = value.toDate();
  if (value instanceof Date && !isNaN(value.getTime())) return formatDateValue(value);
  if (typeof value === 'string') {
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return value.split('-').reverse().join('-');
    }
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(value)) {
      return value.replace(/\//g, '-');
    }
    const parsed = new Date(value);
    if (!isNaN(parsed.getTime())) return formatDateValue(parsed);
    return value;
  }
  return '—';
};
export const fmtMoney = (n: number) => n ? '$' + Number(n).toLocaleString('es-CL') : '—';

export const statusColors: Record<string,string> = {
  'En evaluación':    '#7A6A8A',
  'Pausa':            '#5A6A7A',
  'Aceptado':         '#5C7A5E',
  'En tramitación':   '#B8965A',
  'Resuelto / Cerrado': '#4A5E4A',
  'Desistido':        '#8A5050',
  'Inviable':         '#6A4A4A',
  'Contacto':         '#4A6A8A',
  'Solo Consulta':    '#4A8A7A',
};

/* ── User data ────────────────────────────────────── */
export async function getUserData(uid: string) {
  const snap = await getDoc(doc(db, 'usuarios', uid));
  return snap.exists() ? snap.data() : { rol: 'captadora', nombre: '' };
}

/* ── Load casos ───────────────────────────────────── */
export async function loadCasos(rol: string, uid: string) {
  let q;
  if (rol === 'admin' || rol === 'abogada' || rol === 'procuradora') {
    // procuradora tiene acceso de lectura a todos los casos, igual que admin y abogada
    q = query(collection(db, 'casos'), orderBy('createdAt', 'desc'));
  } else if (rol === 'abogada1') {
    // abogada1 solo ve sus propios casos
    q = query(collection(db, 'casos'), where('abogadaId', '==', uid), orderBy('createdAt', 'desc'));
  } else {
    q = query(collection(db, 'casos'), where('captadoraId', '==', uid), orderBy('createdAt', 'desc'));
  }
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

/* ── Get single caso ──────────────────────────────── */
export async function getCaso(id: string) {
  const snap = await getDoc(doc(db, 'casos', id));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
}

/* ── Get caso by code (for client portal) ───────── */
export async function getCasoByCode(code: string) {
  const q = query(collection(db, 'casos'), where('codigoCaso', '==', code));
  const snap = await getDocs(q);
  if (snap.empty) return null;
  const doc = snap.docs[0];
  return { id: doc.id, ...doc.data() } as any;
}

/* ── Update field ─────────────────────────────────── */
export async function updateCampo(id: string, field: string, value: any) {
  await updateDoc(doc(db, 'casos', id), { [field]: value });
}

/* ── Update estado ────────────────────────────────── */
export async function updateEstado(id: string, estado: string, prevEstado?: string, hasFechaAceptado?: boolean) {
  const updates: Record<string, any> = { estado, updatedAt: serverTimestamp() };
  if (estado === 'Aceptado' && prevEstado !== 'Aceptado') {
    // fechaAceptado: se fija solo la primera vez (referencia histórica, nunca cambia)
    if (!hasFechaAceptado) updates.fechaAceptado = serverTimestamp();
    // fechaUltimoAceptado: se actualiza cada vez, refleja la aceptación más reciente
    updates.fechaUltimoAceptado = serverTimestamp();
  }
  await updateDoc(doc(db, 'casos', id), updates);
}

/* ── Audiencias ───────────────────────────────────── */
export async function addAudiencia(id: string, aud: any) {
  const caso = await getCaso(id);
  const audiencias = (caso as any)?.audiencias || [];
  audiencias.push(aud);
  await updateDoc(doc(db, 'casos', id), { audiencias });
}

export async function removeAudiencia(id: string, index: number) {
  const caso = await getCaso(id);
  const audiencias = (caso as any)?.audiencias || [];
  audiencias.splice(index, 1);
  await updateDoc(doc(db, 'casos', id), { audiencias });
}

// Actualiza campos puntuales de una audiencia existente (p. ej. guardar el
// gcalEventId tras sincronizar con Google Calendar).
export async function updateAudiencia(id: string, index: number, patch: Record<string, any>) {
  const caso = await getCaso(id);
  const audiencias = (caso as any)?.audiencias || [];
  if (!audiencias[index]) return;
  audiencias[index] = { ...audiencias[index], ...patch };
  await updateDoc(doc(db, 'casos', id), { audiencias });
}

/* ── Checklist ────────────────────────────────────── */
export async function addCheckItem(id: string, texto: string, gtasksIds?: Record<string, string>) {
  const item: any = { texto, done: false };
  if (gtasksIds) item.gtasksIds = gtasksIds;
  await updateDoc(doc(db, 'casos', id), { checklist: arrayUnion(item) });
}

export async function toggleCheckItem(id: string, index: number, done: boolean) {
  const caso = await getCaso(id);
  const checklist = (caso as any)?.checklist || [];
  checklist[index].done = done;
  await updateDoc(doc(db, 'casos', id), { checklist });
}

export async function removeCheckItem(id: string, index: number) {
  const caso = await getCaso(id);
  const checklist = (caso as any)?.checklist || [];
  checklist.splice(index, 1);
  await updateDoc(doc(db, 'casos', id), { checklist });
}

export async function updateCheckItem(id: string, index: number, texto: string) {
  const caso = await getCaso(id);
  const checklist = (caso as any)?.checklist || [];
  checklist[index] = { ...checklist[index], texto };
  await updateDoc(doc(db, 'casos', id), { checklist });
}

/* ── Documentos ───────────────────────────────────── */
export async function addDocumento(id: string, docData: any) {
  const caso = await getCaso(id);
  const documentos = (caso as any)?.documentos || [];
  documentos.push({ ...docData, fecha: fmtDate(new Date()) });
  await updateDoc(doc(db, 'casos', id), { documentos });
}

export async function removeDocumento(id: string, index: number) {
  const caso = await getCaso(id);
  const documentos = (caso as any)?.documentos || [];
  documentos.splice(index, 1);
  await updateDoc(doc(db, 'casos', id), { documentos });
}

export async function renameDocumento(id: string, index: number, nuevoNombre: string) {
  const caso = await getCaso(id);
  const documentos = (caso as any)?.documentos || [];
  if (documentos[index]) documentos[index] = { ...documentos[index], nombre: nuevoNombre.trim() };
  await updateDoc(doc(db, 'casos', id), { documentos });
}

/* ── Pagos ────────────────────────────────────────── */
export async function addPago(id: string, pago: any) {
  const caso = await getCaso(id);
  const pagos = (caso as any)?.pagos || [];
  pagos.push(pago);
  await updateDoc(doc(db, 'casos', id), { pagos });
}

export async function removePago(id: string, index: number) {
  const caso = await getCaso(id);
  const pagos = (caso as any)?.pagos || [];
  pagos.splice(index, 1);
  await updateDoc(doc(db, 'casos', id), { pagos });
}

/* ── Nuevo caso ───────────────────────────────────── */
export async function crearCaso(data: any) {
  // La creación (código único, timestamps y alta) se hace en Cloud Function
  // con privilegios admin, para que las reglas puedan restringir la lectura
  // de casos por abogada sin bloquear la verificación de código único.
  const fn = httpsCallable(functions, 'crearCaso');
  const res: any = await fn(data);
  return res.data as { codigo: string; id: string };
}

export async function deleteCaso(id: string) {
  await deleteDoc(doc(db, 'casos', id));
}

/* ── Usuarios ─────────────────────────────────────── */
export async function loadUsuarios() {
  const snap = await getDocs(collection(db, 'usuarios'));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}


export async function loadAbogadas() {
  const [snapAbogadas, snapAbogadas1] = await Promise.all([
    getDocs(query(collection(db, 'usuarios'), where('rol', '==', 'abogada'))),
    getDocs(query(collection(db, 'usuarios'), where('rol', '==', 'abogada1'))),
  ]);
  return [...snapAbogadas.docs, ...snapAbogadas1.docs].map(d => ({ id: d.id, ...d.data() }));
}

/* ── Contactos web (formulario público) ───────────── */

/* El lead se guarda junto a la atribución del clic que lo trajo. Ese gclid es
   lo que permite, cuando el caso se firma, subir la conversión a Google Ads
   con su valor real y que la puja optimice hacia casos y no hacia clics. Sin
   guardarlo aquí, la relación entre campaña y honorarios se pierde para
   siempre en el momento en que la persona envía el formulario. */
export async function crearContactoWeb(data: any) {
  const atribucion = getAtribucion();
  await addDoc(collection(db, 'contactos_web'), {
    ...data,
    ...(atribucion ? { atribucion, origen: atribucion.variante || atribucion.utm_campaign || 'ads' } : {}),
    createdAt: serverTimestamp(),
  });
}

export async function loadContactosWeb() {
  const snap = await getDocs(collection(db, 'contactos_web'));
  return snap.docs
    .map(d => ({ id: d.id, _coleccion: 'contactos_web', ...d.data() }))
    .sort((a: any, b: any) => {
      const da = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt || 0);
      const db2 = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt || 0);
      return db2.getTime() - da.getTime();
    });
}

export async function updateContactoWeb(id: string, field: string, value: any) {
  await updateDoc(doc(db, 'contactos_web', id), { [field]: value });
}

/* ── Cambiar abogada ──────────────────────────────── */
export async function cambiarAbogada(casoId: string, abogadaId: string, abogadaNombre: string) {
  await updateDoc(doc(db, 'casos', casoId), {
    abogadaId,
    abogada: abogadaNombre,
    updatedAt: serverTimestamp(),
  });
}

/* ── Captadoras (todos los roles excepto jans.torruella@gmail.com) ── */
export async function loadCaptadoras() {
  const snap = await getDocs(collection(db, 'usuarios'));
  return snap.docs
    .map(d => ({ id: d.id, ...d.data() as any }))
    .filter((u: any) => u.email !== 'jans.torruella@gmail.com');
}

export async function cambiarCaptadora(casoId: string, captadoraId: string, captadoraNombre: string) {
  await updateDoc(doc(db, 'casos', casoId), {
    captadoraId,
    captadora: captadoraNombre,
    updatedAt: serverTimestamp(),
  });
}

/* ── Reportes ─────────────────────────────────────── */
export async function loadReportesData() {
  const snap = await getDocs(collection(db, 'casos'));
  return snap.docs.map(d => d.data());
}

/* ── Flujo de Caja Config ─────────────────────────── */
export async function getFlujoCajaConfig() {
  const snap = await getDoc(doc(db, 'config', 'flujoCaja'));
  return snap.exists() ? snap.data() : null;
}

export async function saveFlujoCajaConfig(config: any) {
  await setDoc(doc(db, 'config', 'flujoCaja'), config, { merge: true });
}

/* ── Forecast Config ──────────────────────────────── */
export async function getForecastConfig() {
  const snap = await getDoc(doc(db, 'config', 'forecast'));
  return snap.exists() ? snap.data() : null;
}

export async function saveForecastConfig(config: any) {
  await setDoc(doc(db, 'config', 'forecast'), config, { merge: true });
}

/* ── Notificaciones ───────────────────────────────── */
export async function crearNotificacion(data: {
  para: string;
  de: string;
  deNombre: string;
  casoId: string;
  casoNombre: string;
  codigoCaso: string;
  mensaje: string;
  plazo?: string;
}) {
  await addDoc(collection(db, 'notificaciones'), {
    ...data,
    leida: false,
    realizado: false,
    createdAt: serverTimestamp(),
  });
}

export async function actualizarRealizadaPorTexto(casoId: string, texto: string, done: boolean, uid: string) {
  const [snapPara, snapDe] = await Promise.all([
    getDocs(query(collection(db, 'notificaciones'), where('para', '==', uid))),
    getDocs(query(collection(db, 'notificaciones'), where('de', '==', uid))),
  ]);
  const seen = new Set<string>();
  const toUpdate = [...snapPara.docs, ...snapDe.docs].filter(d => {
    if (seen.has(d.id)) return false;
    seen.add(d.id);
    const data = d.data();
    return data.casoId === casoId && data.mensaje === texto;
  });
  await Promise.all(toUpdate.map(d => updateDoc(d.ref, { realizado: done })));
}

export async function actualizarNotificacionesMensaje(casoId: string, mensajeViejo: string, mensajeNuevo: string, plazo?: string) {
  const q = query(collection(db, 'notificaciones'), where('casoId', '==', casoId));
  const snap = await getDocs(q);
  await Promise.all(
    snap.docs
      .filter(d => d.data().mensaje === mensajeViejo)
      .map(d => updateDoc(d.ref, { mensaje: mensajeNuevo, plazo: plazo || null }))
  );
}

export async function eliminarNotificacion(notifId: string) {
  await deleteDoc(doc(db, 'notificaciones', notifId));
}

export async function marcarRealizada(notifId: string, casoId: string, texto: string, done: boolean) {
  await updateDoc(doc(db, 'notificaciones', notifId), { realizado: done, leida: true });
  const caso = await getCaso(casoId);
  const checklist = (caso as any)?.checklist || [];
  const index = checklist.findIndex((item: any) => item.texto === texto);
  if (index >= 0) {
    checklist[index].done = done;
    await updateDoc(doc(db, 'casos', casoId), { checklist });
  }
}

export async function limpiarNotificacionesViejas(uid: string) {
  const cutoff = Date.now() - 20 * 24 * 60 * 60 * 1000;
  const q = query(collection(db, 'notificaciones'), where('para', '==', uid));
  const snap = await getDocs(q);
  await Promise.all(
    snap.docs
      .filter(d => {
        const ts = d.data().createdAt;
        const date = ts?.toDate ? ts.toDate() : new Date(ts || 0);
        return date.getTime() < cutoff;
      })
      .map(d => deleteDoc(d.ref))
  );
}

export function subscribeNotificaciones(uid: string, cb: (notifs: any[]) => void) {
  const q = query(
    collection(db, 'notificaciones'),
    where('para', '==', uid),
    orderBy('createdAt', 'desc')
  );
  return onSnapshot(q,
    snap => { cb(snap.docs.map(d => ({ id: d.id, ...d.data() }))); },
    err => { console.error('[notificaciones] Error en suscripción:', err.code, err.message); }
  );
}

export async function marcarNotificacionLeida(notifId: string) {
  await updateDoc(doc(db, 'notificaciones', notifId), { leida: true });
}

export async function marcarTodasLeidas(uid: string) {
  const q = query(
    collection(db, 'notificaciones'),
    where('para', '==', uid),
    where('leida', '==', false)
  );
  const snap = await getDocs(q);
  await Promise.all(snap.docs.map(d => updateDoc(d.ref, { leida: true })));
}

/* ── Asistencia captadoras ────────────────────────── */
export async function loadAsistenciaMes(mes: number, anio: number) {
  const mesStr = String(mes + 1).padStart(2, '0');
  const q = query(
    collection(db, 'asistencia'),
    where('fecha', '>=', `${anio}-${mesStr}-01`),
    where('fecha', '<=', `${anio}-${mesStr}-31`)
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

export async function marcarAsistencia(
  captadoraId: string,
  captadoraNombre: string,
  fecha: string,
  marcadoPorId: string,
  marcadoPorNombre: string,
  valor: number = 1
) {
  const id = `${captadoraId}_${fecha}`;
  await setDoc(doc(db, 'asistencia', id), {
    captadoraId,
    captadoraNombre,
    fecha,
    valor,
    marcadoPorId,
    marcadoPorNombre,
    createdAt: serverTimestamp(),
  });
}

export async function desmarcarAsistencia(captadoraId: string, fecha: string) {
  await deleteDoc(doc(db, 'asistencia', `${captadoraId}_${fecha}`));
}

/* ── Cierre de mes captadoras ─────────────────────── */
// mesKey formato: "YYYY-M" (ej: "2025-5" para junio 2025)
export async function cerrarMesCaptadoras(casosIds: string[], mesKey: string) {
  if (casosIds.length === 0) return;
  const batch = writeBatch(db);
  casosIds.forEach(id => {
    batch.update(doc(db, 'casos', id), { mesContabilizado: mesKey });
  });
  await batch.commit();
}
